import React, { useState, useMemo } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import '../styles/Overview.css';

function getMondayOfWeek(weeksAgo = 0) {
  const d = new Date();
  const day = d.getUTCDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setUTCDate(d.getUTCDate() + diff - weeksAgo * 7);
  return d.toISOString().slice(0, 10);
}

function getWeekKey(ta) {
  return ta.weekStartDate?.toDate
    ? ta.weekStartDate.toDate().toISOString().slice(0, 10)
    : (ta.weekStartDate?.slice?.(0, 10) ?? '');
}

// SVG icons
const IconRefresh = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/>
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
  </svg>
);

const IconCopy = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
  </svg>
);

const IconWarning = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
    <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
  </svg>
);

const IconCritical = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
  </svg>
);

const IconStar = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
  </svg>
);

const IconBulb = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="9" y1="18" x2="15" y2="18"/><line x1="10" y1="22" x2="14" y2="22"/>
    <path d="M15.09 14c.18-.98.65-1.74 1.41-2.5A4.65 4.65 0 0 0 18 8 6 6 0 0 0 6 8c0 1 .23 2.23 1.5 3.5A4.61 4.61 0 0 1 8.91 14"/>
  </svg>
);

const IconChart = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/>
  </svg>
);

const IconHappy = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/><path d="M8 13s1.5 2 4 2 4-2 4-2"/>
    <line x1="9" y1="9" x2="9.01" y2="9"/><line x1="15" y1="9" x2="15.01" y2="9"/>
  </svg>
);

const IconSleep = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
  </svg>
);

const IconCheck = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);

const IconLock = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2"/>
    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
  </svg>
);

function OverviewSection({ companyData, teams, approvedEmployees, pendingUsers, teamAverages, billingOverLimit }) {
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [localCompanyCode, setLocalCompanyCode] = useState(companyData?.companyCode);

  const twoWeekKeys = useMemo(() => [getMondayOfWeek(1), getMondayOfWeek(2)], []);

  const teamAlerts = useMemo(() => {
    const redZoneAlerts = [];
    const greenZoneAlerts = [];

    teams.forEach(team => {
      const relevant = teamAverages.filter(ta => ta.teamId === team.id && twoWeekKeys.includes(getWeekKey(ta)));
      if (relevant.length === 0) return;
      const totalSurveys = relevant.reduce((a, b) => a + (b.surveyCount || 0), 0);
      if (totalSurveys === 0) return;
      const avgHappiness = relevant.reduce((a, b) => a + (b.avgHappiness * (b.surveyCount || 0)), 0) / totalSurveys;
      const avgTiredness = relevant.reduce((a, b) => a + (b.avgTirednessOverall * (b.surveyCount || 0)), 0) / totalSurveys;

      if (avgHappiness < 3.0) redZoneAlerts.push({ teamId: team.id, teamName: team.name, type: 'happiness', zone: 'red', value: avgHappiness, surveyCount: totalSurveys, severity: avgHappiness < 2.0 ? 'critical' : 'warning' });
      if (avgTiredness > 2.5) redZoneAlerts.push({ teamId: team.id, teamName: team.name, type: 'tiredness', zone: 'red', value: avgTiredness, surveyCount: totalSurveys, severity: avgTiredness > 4.0 ? 'critical' : 'warning' });
      if (avgHappiness >= 3.7) greenZoneAlerts.push({ teamId: team.id, teamName: team.name, type: 'happiness', zone: 'green', value: avgHappiness, surveyCount: totalSurveys });
      if (avgTiredness <= 1.9) greenZoneAlerts.push({ teamId: team.id, teamName: team.name, type: 'tiredness', zone: 'green', value: avgTiredness, surveyCount: totalSurveys });
    });

    redZoneAlerts.sort((a, b) => {
      if (a.severity === 'critical' && b.severity !== 'critical') return -1;
      if (a.severity !== 'critical' && b.severity === 'critical') return 1;
      if (a.type === 'happiness') return a.value - b.value;
      return b.value - a.value;
    });

    return { redZone: redZoneAlerts, greenZone: greenZoneAlerts };
  }, [teamAverages, teams, twoWeekKeys]);

  const totalSurveyCount = useMemo(() => teamAverages.reduce((a, b) => a + (b.surveyCount || 0), 0), [teamAverages]);

  const generateCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let code = '';
    for (let i = 0; i < 6; i++) code += chars.charAt(Math.floor(Math.random() * chars.length));
    return code;
  };

  const handleRegenerateCode = async () => {
    const confirmed = window.confirm(
      'Are you sure you want to regenerate your company code?\n\n' +
      '• The old code will stop working for new requests\n' +
      '• Current employees will not be affected\n' +
      '• Share the new code with future employees'
    );
    if (!confirmed) return;
    setIsRegenerating(true);
    try {
      const newCode = generateCode();
      await updateDoc(doc(db, 'companies', companyData.id), { companyCode: newCode, codeRegeneratedAt: new Date().toISOString(), previousCode: companyData.companyCode });
      await updateDoc(doc(db, 'companyCodes', companyData.id), { companyCode: newCode });
      setLocalCompanyCode(newCode);
      alert(`New company code: ${newCode}\n\nShare this with new employees.`);
    } catch (error) {
      console.error('Error regenerating code:', error);
      alert('Failed to regenerate code. Please try again.');
    } finally {
      setIsRegenerating(false);
    }
  };

  const handleCopyCode = async () => {
    const code = localCompanyCode || companyData?.companyCode;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(code);
        alert('Company code copied!');
      } else {
        const el = document.createElement('textarea');
        el.value = code;
        el.style.position = 'fixed'; el.style.left = '-999999px';
        document.body.appendChild(el); el.focus(); el.select();
        try { document.execCommand('copy'); alert('Company code copied!'); }
        catch { alert(`Copy failed. Code: ${code}`); }
        document.body.removeChild(el);
      }
    } catch { alert(`Copy failed. Code: ${code}`); }
  };

  const getAlertMessage = (alert) => {
    if (alert.zone === 'green') {
      return alert.type === 'happiness'
        ? `This team is showing excellent happiness levels (${alert.value.toFixed(2)}/5). Keep up the great work!`
        : `This team is maintaining low tiredness levels (${alert.value.toFixed(2)}/5). Excellent work-life balance!`;
    }
    if (alert.type === 'happiness') {
      return alert.severity === 'critical'
        ? `This team is showing critically low happiness levels (${alert.value.toFixed(2)}/5). Immediate attention recommended.`
        : `This team's happiness levels are below the optimal range (${alert.value.toFixed(2)}/5). Consider checking in with team members.`;
    }
    return alert.severity === 'critical'
      ? `This team is experiencing critically high tiredness levels (${alert.value.toFixed(2)}/5). Immediate intervention recommended.`
      : `This team's tiredness levels are elevated (${alert.value.toFixed(2)}/5). Monitor workload and rest patterns.`;
  };

  const getRecommendation = (alert) => {
    if (alert.zone === 'green') {
      return alert.type === 'happiness'
        ? 'Continue current practices. Consider documenting what is working well for this team to share with others.'
        : 'Team workload appears well-balanced. Maintain current scheduling and resource allocation practices.';
    }
    if (alert.type === 'happiness') {
      return alert.severity === 'critical'
        ? 'Schedule one-on-one meetings with team members immediately. Review current projects for stress factors.'
        : 'Engage with the team to understand concerns. Consider team meetings to address morale and identify areas for improvement.';
    }
    return alert.severity === 'critical'
      ? 'Review team workload immediately. Consider redistributing tasks, extending deadlines, or providing additional resources.'
      : 'Monitor team workload and schedules. Encourage breaks and time off. Check for overtime patterns and project deadlines.';
  };

  return (
    <>
      <div className="dashboard-header">
        <div>
          <h1>{companyData?.name}</h1>
          <p>Manage your teams and employee requests</p>
        </div>
      </div>

      <div className="company-code-card">
        <div className="code-header">
          <h3>Company Code</h3>
          <button className="btn-regenerate" onClick={handleRegenerateCode} disabled={isRegenerating}>
            <IconRefresh />
            {isRegenerating ? 'Generating...' : 'Regenerate'}
          </button>
        </div>
        <div className="code-display">
          <span className="code">{localCompanyCode || companyData?.companyCode}</span>
          <button className="btn-copy" onClick={handleCopyCode}>
            <IconCopy />
            Copy
          </button>
        </div>
        <p className="code-hint">Share this code with employees to let them request access to your company</p>
        {companyData?.codeRegeneratedAt && (
          <p className="code-last-changed">Last regenerated: {new Date(companyData.codeRegeneratedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
        )}
      </div>

      <div className="dashboard-stats">
        <div className="stat-card no-hover"><h3>Active Teams</h3><p className="stat-number">{teams.length}</p></div>
        <div className="stat-card no-hover"><h3>Total Employees</h3><p className="stat-number">{approvedEmployees.length}</p></div>
        <div className="stat-card no-hover"><h3>Pending Requests</h3><p className="stat-number">{pendingUsers.length}</p></div>
        <div className="stat-card no-hover"><h3>Total Surveys</h3><p className="stat-number">{totalSurveyCount.toLocaleString()}</p></div>
      </div>

      {billingOverLimit ? (
        <div className="alerts-section">
          <div className="billing-gate-banner">
            <IconLock />
            <div>
              <strong>Subscription required to view team alerts</strong>
              <p>Your current plan limit has been reached or a payment has failed. Visit the Payments &amp; Billing section to update your subscription and restore full access.</p>
            </div>
          </div>
        </div>
      ) : (
        <>
          {teamAlerts.redZone.length > 0 && (
            <div className="alerts-section">
              <div className="alerts-header">
                <div>
                  <h3 className="alerts-title alerts-title-red">
                    <IconWarning /> Teams Requiring Attention
                  </h3>
                  <p className="alerts-subtitle">Based on the last 2 completed weeks of survey data</p>
                </div>
                <div className="alerts-legend">
                  <span className="legend-item critical"><IconCritical /> Critical</span>
                  <span className="legend-item warning"><IconWarning /> Warning</span>
                </div>
              </div>
              <div className="alerts-list">
                {teamAlerts.redZone.map((alert, index) => (
                  <div key={`${alert.teamId}-${alert.type}-${index}`} className={`alert-card ${alert.severity}`}>
                    <div className="alert-header">
                      <div className="alert-title-section">
                        <h4>
                          {alert.severity === 'critical' ? <IconCritical /> : <IconWarning />}
                          {alert.teamName}
                        </h4>
                        <span className="alert-team-name">{alert.type === 'happiness' ? 'Low Happiness' : 'High Tiredness'}</span>
                      </div>
                      <div className="alert-badge-group">
                        <span className={`alert-category-badge ${alert.type}`}>
                          {alert.type === 'happiness' ? <IconHappy /> : <IconSleep />}
                          {alert.type}
                        </span>
                        <span className="alert-survey-count">{alert.surveyCount} responses</span>
                      </div>
                    </div>
                    <div className="alert-body">
                      <p className="alert-message">{getAlertMessage(alert)}</p>
                      <div className="alert-metrics">
                        <div className="metric-simple">
                          <div className="metric-label">{alert.type === 'happiness' ? 'Average Happiness' : 'Average Tiredness'}</div>
                          <div className="metric-value-large" style={{ color: alert.severity === 'critical' ? '#dc2626' : '#f59e0b' }}>
                            {alert.value.toFixed(2)} / 5
                          </div>
                          <div className="metric-status">
                            {alert.type === 'happiness'
                              ? (alert.severity === 'critical' ? 'Critically Low (< 2.0)' : 'Below Optimal (< 3.0)')
                              : (alert.severity === 'critical' ? 'Critically High (> 4.0)' : 'Elevated (> 2.5)')}
                          </div>
                        </div>
                      </div>
                      <div className="alert-recommendation">
                        <span className="recommendation-icon"><IconBulb /></span>
                        <span className="recommendation-text">{getRecommendation(alert)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {teamAlerts.greenZone.length > 0 && (
            <div className="alerts-section green-section">
              <div className="alerts-header">
                <div>
                  <h3 className="alerts-title alerts-title-green">
                    <IconStar /> Top Performing Teams
                  </h3>
                  <p className="alerts-subtitle">Based on the last 2 completed weeks of survey data</p>
                </div>
                <div className="alerts-legend">
                  <span className="legend-item positive"><IconCheck /> Excellent</span>
                </div>
              </div>
              <div className="alerts-list">
                {teamAlerts.greenZone.map((alert, index) => (
                  <div key={`${alert.teamId}-${alert.type}-green-${index}`} className="alert-card positive">
                    <div className="alert-header">
                      <div className="alert-title-section">
                        <h4><IconStar /> {alert.teamName}</h4>
                        <span className="alert-team-name">{alert.type === 'happiness' ? 'High Happiness' : 'Low Tiredness'}</span>
                      </div>
                      <div className="alert-badge-group">
                        <span className={`alert-category-badge ${alert.type}`}>
                          {alert.type === 'happiness' ? <IconHappy /> : <IconSleep />}
                          {alert.type}
                        </span>
                        <span className="alert-survey-count">{alert.surveyCount} responses</span>
                      </div>
                    </div>
                    <div className="alert-body">
                      <p className="alert-message">{getAlertMessage(alert)}</p>
                      <div className="alert-metrics">
                        <div className="metric-simple">
                          <div className="metric-label">{alert.type === 'happiness' ? 'Average Happiness' : 'Average Tiredness'}</div>
                          <div className="metric-value-large" style={{ color: '#059669' }}>{alert.value.toFixed(2)} / 5</div>
                          <div className="metric-status">{alert.type === 'happiness' ? 'Excellent (>= 3.7)' : 'Well-Rested (<= 1.9)'}</div>
                        </div>
                      </div>
                      <div className="alert-recommendation">
                        <span className="recommendation-icon"><IconBulb /></span>
                        <span className="recommendation-text">{getRecommendation(alert)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {teamAlerts.redZone.length === 0 && teamAlerts.greenZone.length === 0 && (
            <div className="alerts-section">
              <div className="alerts-empty-state">
                <div className="empty-icon"><IconChart /></div>
                <h3>All Teams in Normal Range</h3>
                <p>Teams are performing within normal ranges. No critical alerts or exceptional performance to highlight.</p>
                <p className="empty-cta">Visit <strong>Analytics</strong> to view detailed performance metrics for each team.</p>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
}

export default OverviewSection;